/**
 * Unified Entity Palette
 * Modular system for selecting all entity types (NPCs, Mobs, Objects, Spawns, Teleporters)
 * with category-based organization and visual distinction
 */

import React, { useState, useMemo } from 'react';
import { MOB_REGISTRY } from '../../../../shared/MobRegistry';
import { NPC_DATABASE } from '../../../../shared/NPCData';
import { getEntitySize } from '../../../../shared/EntitySizes';

export type EntityCategory = 'all' | 'npc' | 'mob' | 'spawn' | 'object' | 'teleporter';

export interface EntityDefinition {
  id: string;
  name: string;
  type: EntityCategory;
  subType?: string;
  icon: string;
  color: string;
  description?: string;
  width: number;
  height: number;
  meta?: Record<string, any>;
}

interface EntityPaletteProps {
  currentEntityId: string | null;
  currentEntityType: EntityCategory | null;
  onEntitySelect: (id: string, type: EntityCategory) => void;
}

// Static entity definitions (spawns, objects, teleporters)
const STATIC_ENTITIES: EntityDefinition[] = [
  {
    id: 'player_spawn',
    name: 'Player Spawn',
    type: 'spawn',
    subType: 'player',
    icon: '▶',
    color: '#32CD32',
    description: 'Where players start',
    width: 2,
    height: 3
  },
  {
    id: 'npc_spawn',
    name: 'NPC Spawn Point',
    type: 'spawn',
    subType: 'npc',
    icon: '👤',
    color: '#00BFFF',
    description: 'Generic NPC spawn location',
    width: 1,
    height: 2
  },
  {
    id: 'mob_spawn',
    name: 'Mob Spawn Point',
    type: 'spawn',
    subType: 'mob',
    icon: '💀',
    color: '#DC143C',
    description: 'Monster spawn location',
    width: 2,
    height: 2
  },
  {
    id: 'teleporter',
    name: 'Teleporter',
    type: 'teleporter',
    icon: '⬌',
    color: '#9370DB',
    description: 'Transport to another map',
    width: 2,
    height: 3
  },
  {
    id: 'chest_basic',
    name: 'Basic Chest',
    type: 'object',
    subType: 'chest',
    icon: '📦',
    color: '#DAA520',
    description: 'Contains items/gold',
    width: 1,
    height: 1
  },
  {
    id: 'training_dummy',
    name: 'Training Dummy',
    type: 'object',
    subType: 'training',
    icon: '🎯',
    color: '#FFD700',
    description: 'Practice combat',
    width: 1,
    height: 2
  }
];

// Convert NPC database to entity definitions
const npcEntities: EntityDefinition[] = Object.values(NPC_DATABASE).map(npc => ({
  id: npc.id,
  name: npc.name,
  type: 'npc',
  subType: npc.role,
  icon: getNPCIcon(npc.role),
  color: npc.color || getNPCColor(npc.role),
  description: npc.dialogue?.greeting?.substring(0, 40) || 'NPC',
  width: npc.width || 1,
  height: npc.height || 2,
  meta: { role: npc.role, hasDialogue: !!npc.dialogue }
}));

// Convert Mob registry to entity definitions
const mobEntities: EntityDefinition[] = Object.values(MOB_REGISTRY).map(mob => {
  const size = getEntitySize(mob.id);
  return {
    id: mob.id,
    name: mob.name,
    type: 'mob',
    subType: mob.type,
    icon: getMobIcon(mob.type),
    color: mob.color,
    description: `Lv${mob.level} | HP: ${mob.hp} | XP: ${mob.xp}`,
    width: size.width,
    height: size.height,
    meta: { level: mob.level, hp: mob.hp, xp: mob.xp, type: mob.type }
  };
});

// Helper functions
function getNPCIcon(role?: string): string {
  const icons: Record<string, string> = {
    'quest_giver': '!',
    'trainer': '⚔',
    'vendor': '$',
    'guard': '🛡',
    'civilian': '👤',
    'default': '?'
  };
  return icons[role || 'default'] || icons.default;
}

function getNPCColor(role?: string): string {
  const colors: Record<string, string> = {
    'quest_giver': '#FFD700',
    'trainer': '#FF6347',
    'vendor': '#32CD32',
    'guard': '#4682B4',
    'civilian': '#DDA0DD',
    'default': '#87CEEB'
  };
  return colors[role || 'default'] || colors.default;
}

function getMobIcon(faction?: string): string {
  const icons: Record<string, string> = {
    'hostile': '💀',
    'neutral': '🐗',
    'friendly': '🐰',
    'boss': '👹',
    'default': '👾'
  };
  return icons[faction || 'default'] || icons.default;
}

const CATEGORY_CONFIG: Record<EntityCategory, { label: string; icon: string; color: string }> = {
  all: { label: 'All', icon: '📋', color: '#888' },
  npc: { label: 'NPCs', icon: '👤', color: '#87CEEB' },
  mob: { label: 'Monsters', icon: '💀', color: '#FF6347' },
  spawn: { label: 'Spawns', icon: '🎯', color: '#32CD32' },
  object: { label: 'Objects', icon: '📦', color: '#DAA520' },
  teleporter: { label: 'Portals', icon: '⬌', color: '#9370DB' }
};

export const EntityPalette: React.FC<EntityPaletteProps> = ({
  currentEntityId,
  currentEntityType,
  onEntitySelect
}) => {
  const [activeCategory, setActiveCategory] = useState<EntityCategory>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Combine all entities
  const allEntities = useMemo(() => {
    return [...STATIC_ENTITIES, ...npcEntities, ...mobEntities];
  }, []);

  // Filter entities by category and search
  const filteredEntities = useMemo(() => {
    return allEntities.filter(entity => {
      const matchesCategory = activeCategory === 'all' || entity.type === activeCategory;
      const matchesSearch = searchQuery === '' ||
        entity.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entity.description?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [allEntities, activeCategory, searchQuery]);

  // Group by type for display
  const groupedEntities = useMemo(() => {
    const groups: Record<string, EntityDefinition[]> = {};
    filteredEntities.forEach(entity => {
      const groupKey = entity.subType || entity.type;
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(entity);
    });
    return groups;
  }, [filteredEntities]);

  const isSelected = (entity: EntityDefinition) => {
    return currentEntityId === entity.id && currentEntityType === entity.type;
  };

  return (
    <div
      className="entity-palette"
      style={{
        width: '280px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        overflow: 'hidden',
        pointerEvents: 'auto',
        maxHeight: '100%',
        backgroundColor: '#34495e',
        color: 'white',
        borderLeft: '1px solid #2c3e50',
        borderRight: '1px solid #2c3e50',
        padding: '8px'
      }}
    >
      {/* Header */}
      <div style={{ padding: '4px 4px 8px' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, fontSize: '14px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '8px' }}>
          <span>🎭</span>
          Entity Palette
        </h3>
        <div className="help-text" style={{ fontSize: '0.75em', opacity: 0.7, marginTop: '5px' }}>
          Select and place entities
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: '0 4px 8px' }}>
        <input
          type="text"
          placeholder="Search entities..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            backgroundColor: '#2c3e50',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
            color: 'white',
            fontSize: '12px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Category Tabs */}
      <div style={{
        backgroundColor: '#2c3e50',
        borderRadius: '6px',
        padding: '8px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        marginBottom: '4px'
      }}>
        <div style={{ width: '100%', fontSize: '11px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: '4px', fontWeight: 'bold' }}>Categories</div>
        {(Object.keys(CATEGORY_CONFIG) as EntityCategory[]).map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            style={{
              flex: '1 0 30%',
              padding: '6px 4px',
              backgroundColor: activeCategory === cat ? 'rgba(255,255,255,0.1)' : 'transparent',
              border: `1px solid ${activeCategory === cat ? 'rgba(255,255,255,0.2)' : 'transparent'}`,
              borderRadius: '4px',
              color: activeCategory === cat ? '#fff' : 'rgba(255,255,255,0.6)',
              fontSize: '11px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              transition: 'all 0.15s ease'
            }}
          >
            <span style={{ fontSize: '14px', color: CATEGORY_CONFIG[cat].color }}>{CATEGORY_CONFIG[cat].icon}</span>
            <span>{CATEGORY_CONFIG[cat].label}</span>
          </button>
        ))}
      </div>

      {/* Entity List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}>
        {Object.entries(groupedEntities).map(([groupKey, entities]) => (
          <div key={groupKey} style={{
            backgroundColor: '#2c3e50',
            borderRadius: '6px',
            padding: '8px'
          }}>
            {/* Group Header */}
            <div style={{
              fontSize: '0.7em',
              textTransform: 'uppercase',
              opacity: 0.6,
              marginBottom: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              letterSpacing: '0.5px'
            }}>
              <span style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: entities[0]?.color || '#888'
              }} />
              {formatGroupName(groupKey)} ({entities.length})
            </div>

            {/* Entity Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '6px'
            }}>
              {entities.map(entity => (
                <EntityCard
                  key={entity.id}
                  entity={entity}
                  selected={isSelected(entity)}
                  onClick={() => onEntitySelect(entity.id, entity.type)}
                />
              ))}
            </div>
          </div>
        ))}

        {filteredEntities.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '30px 10px',
            opacity: 0.5,
            fontSize: '0.85em'
          }}>
            <div style={{ fontSize: '2em', marginBottom: '10px' }}>🔍</div>
            No entities found
          </div>
        )}
      </div>
    </div>
  );
};

// Entity Card Component
interface EntityCardProps {
  entity: EntityDefinition;
  selected: boolean;
  onClick: () => void;
}

const EntityCard: React.FC<EntityCardProps> = ({ entity, selected, onClick }) => {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '8px',
        backgroundColor: selected ? 'rgba(255,255,255,0.1)' : 'transparent',
        border: `1px solid ${selected ? entity.color : 'rgba(255,255,255,0.05)'}`,
        borderRadius: '4px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '6px',
        transition: 'all 0.15s ease',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Type Badge */}
      <div style={{
        position: 'absolute',
        top: '4px',
        right: '4px',
        fontSize: '0.65em',
        padding: '2px 5px',
        background: 'rgba(0,0,0,0.5)',
        borderRadius: '3px',
        color: entity.color,
        fontWeight: 'bold',
        textTransform: 'uppercase'
      }}>
        {entity.type.charAt(0)}
      </div>

      {/* Icon */}
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${entity.color}40, ${entity.color})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.3em',
        boxShadow: selected ? `0 0 12px ${entity.color}80` : 'none',
        border: `2px solid ${selected ? entity.color : 'rgba(255,255,255,0.2)'}`
      }}>
        {entity.icon}
      </div>

      {/* Name */}
      <div style={{
        fontSize: '0.75em',
        fontWeight: 'bold',
        textAlign: 'center',
        lineHeight: '1.2',
        color: selected ? '#fff' : 'rgba(255,255,255,0.9)'
      }}>
        {entity.name}
      </div>

      {/* Size Badge */}
      <div style={{
        fontSize: '0.65em',
        opacity: 0.6,
        background: 'rgba(0,0,0,0.4)',
        padding: '2px 6px',
        borderRadius: '4px'
      }}>
        {entity.width}×{entity.height}
      </div>

      {/* Description (truncated) */}
      {entity.description && (
        <div style={{
          fontSize: '0.65em',
          opacity: 0.5,
          textAlign: 'center',
          lineHeight: '1.2',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {entity.description}
        </div>
      )}
    </div>
  );
};



function formatGroupName(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}

export default EntityPalette;
